import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../App';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Trophy, TrendingUp, TrendingDown, Clock, CheckCircle, XCircle, 
  ChevronDown, ChevronUp, Calendar, Target, Flame, Award, ArrowLeft
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Streak badge component
const StreakBadge = ({ streak }) => {
  if (streak < 3) return null;
  
  if (streak >= 10) {
    return (
      <span className="bg-gradient-to-r from-yellow-500 to-amber-600 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1 animate-pulse">
        <Trophy className="w-3 h-3" /> LEGENDARY ({streak})
      </span>
    );
  }
  if (streak >= 5) {
    return (
      <span className="bg-gradient-to-r from-orange-500 to-red-600 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
        <Flame className="w-3 h-3" /> ON FIRE ({streak})
      </span>
    );
  }
  return (
    <span className="bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1">
      <TrendingUp className="w-3 h-3" /> HOT STREAK ({streak})
    </span>
  );
};

// Pick result indicator
const PickResult = ({ isCorrect }) => {
  if (isCorrect === null || isCorrect === undefined) {
    return (
      <span className="flex items-center gap-1 text-gray-400 text-sm">
        <Clock className="w-4 h-4" /> Pending
      </span>
    );
  }
  if (isCorrect) {
    return (
      <span className="flex items-center gap-1 text-green-500 text-sm font-medium">
        <CheckCircle className="w-4 h-4" /> Correct
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-red-500 text-sm font-medium">
      <XCircle className="w-4 h-4" /> Incorrect
    </span>
  );
};

// Week accordion component
const WeekAccordion = ({ week, isExpanded, onToggle }) => {
  const hasResults = week.stats.correct + week.stats.incorrect > 0;
  
  return (
    <div className="bg-white/5 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10 mb-4">
      {/* Week Header */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
        data-testid={`week-${week.week_number}-toggle`}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center text-white font-bold">
            {week.week_number}
          </div>
          <div className="text-left">
            <h3 className="text-white font-bold text-lg">{week.week_title}</h3>
            <div className="flex items-center gap-2 text-sm">
              {hasResults ? (
                <>
                  <span className="text-green-400">{week.stats.correct} correct</span>
                  <span className="text-gray-500">•</span>
                  <span className="text-red-400">{week.stats.incorrect} wrong</span>
                  {week.stats.pending > 0 && (
                    <>
                      <span className="text-gray-500">•</span>
                      <span className="text-gray-400">{week.stats.pending} pending</span>
                    </>
                  )}
                </>
              ) : (
                <span className="text-gray-400">{week.stats.pending} picks pending</span>
              )}
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {hasResults && (
            <div className={`text-2xl font-bold ${
              week.stats.percentage >= 70 ? 'text-green-400' :
              week.stats.percentage >= 50 ? 'text-yellow-400' :
              'text-red-400'
            }`}>
              {week.stats.percentage}%
            </div>
          )}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>
      
      {/* Week Picks */}
      {isExpanded && (
        <div className="border-t border-white/10 px-4 py-4">
          <div className="space-y-3">
            {week.picks.map((pick, idx) => (
              <div 
                key={pick.pick_id} 
                className={`rounded-lg p-4 ${
                  pick.is_correct === true ? 'bg-green-900/20 border border-green-500/30' :
                  pick.is_correct === false ? 'bg-red-900/20 border border-red-500/30' :
                  'bg-white/5 border border-white/10'
                }`}
                data-testid={`pick-${idx}`}
              >
                <div className="flex items-center justify-between">
                  {/* Matchup */}
                  <div className="flex items-center gap-4">
                    {/* Away Team */}
                    <div className={`flex items-center gap-2 ${
                      pick.selected_team?.id === pick.game.away_team.id 
                        ? 'ring-2 ring-blue-500 rounded-lg p-1' 
                        : ''
                    }`}>
                      <img 
                        src={pick.game.away_team.logo_url} 
                        alt={pick.game.away_team.name}
                        className="w-10 h-10 object-contain"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=?'; }}
                      />
                      <div className="text-left">
                        <div className="text-white font-medium text-sm">{pick.game.away_team.name}</div>
                        {pick.game.away_team.score !== null && pick.game.away_team.score !== undefined && (
                          <div className="text-gray-400 text-xs">{pick.game.away_team.score}</div>
                        )}
                      </div>
                    </div>
                    
                    <span className="text-gray-500 text-sm">@</span>
                    
                    {/* Home Team */}
                    <div className={`flex items-center gap-2 ${
                      pick.selected_team?.id === pick.game.home_team.id 
                        ? 'ring-2 ring-blue-500 rounded-lg p-1' 
                        : ''
                    }`}>
                      <img 
                        src={pick.game.home_team.logo_url} 
                        alt={pick.game.home_team.name}
                        className="w-10 h-10 object-contain"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/40?text=?'; }}
                      />
                      <div className="text-left">
                        <div className="text-white font-medium text-sm">{pick.game.home_team.name}</div>
                        {pick.game.home_team.score !== null && pick.game.home_team.score !== undefined && (
                          <div className="text-gray-400 text-xs">{pick.game.home_team.score}</div>
                        )}
                      </div>
                    </div>
                    
                    {pick.game.is_game_of_week && (
                      <span className="bg-yellow-500/20 text-yellow-400 text-xs px-2 py-1 rounded">
                        GOTW
                      </span>
                    )}
                  </div>
                  
                  {/* Result */}
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-gray-400 text-xs">Your Pick:</div>
                      <div className="text-white font-medium">{pick.selected_team?.name || 'Unknown'}</div>
                    </div>
                    <PickResult isCorrect={pick.is_correct} />
                  </div>
                </div>
                
                {/* Game label if exists */}
                {pick.game.game_label && (
                  <div className="mt-2 text-gray-500 text-xs">
                    {pick.game.game_label}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function PickHistoryPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedWeeks, setExpandedWeeks] = useState(new Set());
  
  const fetchHistory = useCallback(async () => {
    if (!user?.user_id) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/pickem/history/${user.user_id}`);
      if (!response.ok) throw new Error('Failed to fetch history');
      const data = await response.json();
      setHistory(data);
      
      // Auto-expand the most recent week with picks
      if (data.weeks && data.weeks.length > 0) {
        const latestWeek = data.weeks[data.weeks.length - 1];
        setExpandedWeeks(new Set([latestWeek.week_id]));
      }
    } catch (error) {
      console.error('Error fetching pick history:', error);
      toast.error('Failed to load pick history');
    } finally {
      setLoading(false);
    }
  }, [user?.user_id]);
  
  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);
  
  const toggleWeek = (weekId) => {
    setExpandedWeeks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(weekId)) {
        newSet.delete(weekId);
      } else {
        newSet.add(weekId);
      }
      return newSet;
    });
  };
  
  const expandAll = () => {
    if (history?.weeks) {
      setExpandedWeeks(new Set(history.weeks.map(w => w.week_id)));
    }
  };
  
  const collapseAll = () => {
    setExpandedWeeks(new Set());
  };
  
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#071b45] to-[#0a2463] flex items-center justify-center">
        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-8 text-center max-w-md">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Login Required</h2>
          <p className="text-gray-400 mb-6">Sign in to view your pick history and track your performance.</p>
          <Link 
            to="/swac-pickem"
            className="inline-block bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-3 rounded-lg transition-colors"
          >
            Go to SWAC Pick'em
          </Link>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#071b45] to-[#0a2463] flex items-center justify-center">
        <div className="text-white text-xl">Loading pick history...</div>
      </div>
    );
  }
  
  if (!history || history.total_picks === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#071b45] to-[#0a2463] flex items-center justify-center">
        <div className="bg-white/5 backdrop-blur-sm rounded-xl p-8 text-center max-w-md">
          <Target className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">No Picks Yet</h2>
          <p className="text-gray-400 mb-6">You haven't made any picks yet. Head to SWAC Pick'em to make your predictions!</p>
          <Link 
            to="/swac-pickem"
            className="inline-block bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-3 rounded-lg transition-colors"
          >
            Make Your Picks
          </Link>
        </div>
      </div>
    );
  }
  
  const { summary, weeks } = history;
  
  return (
    <>
      <Helmet>
        <title>Pick History | SWAC Pick'em | SheLovesThee</title>
        <meta name="description" content="View your complete SWAC Pick'em history across all 13 weeks of the season." />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-[#071b45] via-[#0a2463] to-[#071b45]">
        {/* Header */}
        <div className="bg-[#071b45] border-b border-white/10 py-6">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center gap-4 mb-4">
              <button 
                onClick={() => navigate('/swac-pickem')}
                className="text-gray-400 hover:text-white transition-colors flex items-center gap-2"
                data-testid="back-to-pickem"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Pick'em
              </button>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white">
                  📋 Pick History
                </h1>
                <p className="text-gray-400 mt-1">Your complete 2026 season picks</p>
              </div>
              
              {summary.current_streak >= 3 && (
                <StreakBadge streak={summary.current_streak} />
              )}
            </div>
          </div>
        </div>
        
        {/* Summary Cards */}
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {/* Win Rate */}
            <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 backdrop-blur-sm rounded-xl p-4 border border-green-500/30">
              <div className="text-green-400 text-sm font-medium mb-1">Win Rate</div>
              <div className="text-3xl font-bold text-white">{summary.win_percentage}%</div>
              <div className="text-gray-400 text-xs mt-1">
                {summary.total_correct}/{summary.total_correct + summary.total_incorrect} correct
              </div>
            </div>
            
            {/* Current Streak */}
            <div className="bg-gradient-to-br from-orange-900/30 to-orange-800/20 backdrop-blur-sm rounded-xl p-4 border border-orange-500/30">
              <div className="text-orange-400 text-sm font-medium mb-1">Current Streak</div>
              <div className="text-3xl font-bold text-white flex items-center gap-2">
                {summary.current_streak}
                {summary.current_streak >= 3 && <Flame className="w-6 h-6 text-orange-500" />}
              </div>
              <div className="text-gray-400 text-xs mt-1">consecutive wins</div>
            </div>
            
            {/* Total Picks */}
            <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 backdrop-blur-sm rounded-xl p-4 border border-blue-500/30">
              <div className="text-blue-400 text-sm font-medium mb-1">Total Picks</div>
              <div className="text-3xl font-bold text-white">{history.total_picks}</div>
              <div className="text-gray-400 text-xs mt-1">
                {summary.weeks_played} weeks played
              </div>
            </div>
            
            {/* Best Week */}
            <div className="bg-gradient-to-br from-yellow-900/30 to-yellow-800/20 backdrop-blur-sm rounded-xl p-4 border border-yellow-500/30">
              <div className="text-yellow-400 text-sm font-medium mb-1">Best Week</div>
              {summary.best_week ? (
                <>
                  <div className="text-3xl font-bold text-white">Week {summary.best_week.week_number}</div>
                  <div className="text-gray-400 text-xs mt-1">
                    {summary.best_week.percentage}% ({summary.best_week.correct}/{summary.best_week.total})
                  </div>
                </>
              ) : (
                <div className="text-gray-400 text-xl">—</div>
              )}
            </div>
          </div>
          
          {/* Week Controls */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Week by Week
            </h2>
            <div className="flex gap-2">
              <button
                onClick={expandAll}
                className="text-sm text-gray-400 hover:text-white transition-colors px-3 py-1 rounded border border-white/10 hover:border-white/30"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="text-sm text-gray-400 hover:text-white transition-colors px-3 py-1 rounded border border-white/10 hover:border-white/30"
              >
                Collapse All
              </button>
            </div>
          </div>
          
          {/* Week Accordions */}
          <div className="space-y-4" data-testid="weeks-list">
            {weeks.map(week => (
              <WeekAccordion
                key={week.week_id}
                week={week}
                isExpanded={expandedWeeks.has(week.week_id)}
                onToggle={() => toggleWeek(week.week_id)}
              />
            ))}
          </div>
          
          {/* Bottom CTA */}
          <div className="mt-8 text-center">
            <Link
              to="/swac-pickem"
              className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4 rounded-xl transition-colors"
              data-testid="make-picks-btn"
            >
              <Target className="w-5 h-5" />
              Make This Week's Picks
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
